import type { ReactElement } from 'react';
import { useMemo, useState, useCallback } from 'react';
import { DataTable } from '@/components/DataTable';
import type { DataFetchParams, DataTableProps } from '@/components/DataTable';
import type {
  RunningNowListItem,
  RunningNowSortField,
} from '@kirin/api-client/contracts/shadow-ai-running-now';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { queryKeys } from '@/hooks/api/queryKeys';
import { RUNNING_PROCESSES_REFRESH_INTERVAL } from '@/hooks/api/useShadowAiTools';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import {
  ExpandedProcessRow,
  SHADOW_AI_TABLE_SCROLL_CLASS,
  RUNNING_NOW_NOTE,
} from '@/components/shared/shadow-ai';
import {
  TOOL_APPROVAL_SCOPES,
  TOOL_APPROVAL_TABS,
  type ToolApprovalFilter,
} from '@kirin/api-client/contracts/shadow-ai-tool-approvals';
import { createToolApprovalColumn } from '@/components/shared/shadow-ai/toolApprovalColumn';
import { useToolApprovalTable } from '@/components/shared/shadow-ai/useToolApprovalTable';
import { getRunningNowRowLabel } from '@/components/shared/shadow-ai/toolApprovalRowLabels';
import { Skeleton } from '@/components/ui/skeleton';
import { createRunningNowColumns } from './columns';
import { fetchRunningNow } from './api';
import { RUNNING_NOW_TABLE_CONFIG } from './constants';

// Selection is per process row; approval is per tool (shared `endpoint` scope).
const getRunningNowRowId = (row: RunningNowListItem): string => row.id;
const getRunningNowToolId = (row: RunningNowListItem): string => row.toolId;

function buildQueryKeys(
  organizationId: string,
  params: DataFetchParams,
  rootOnly: boolean,
  scopeUserId?: string,
  approval?: ToolApprovalFilter
) {
  const base = queryKeys.runningNow.dataTable({
    organizationId,
    page: params.pagination.pageIndex + 1,
    limit: params.pagination.pageSize,
    filters: params.filters,
    sortBy: params.sort?.field as RunningNowSortField | undefined,
    sortOrder: params.sort?.order,
    search: params.search,
    // KIR-4854: only keyed when the flag is on (undefined drops out).
    ...(approval ? { approval } : {}),
  });
  const withRoot = rootOnly ? [...base, { isRoot: true }] : base;
  return scopeUserId ? [...withRoot, { scopeUserId }] : withRoot;
}

interface DataTableRunningNowContentProps {
  organizationId: string;
  /** Omit on a surface already scoped to one user — the cell renders plain text. */
  onUserClick?: (
    userId: string,
    toolId: string,
    processId: string,
    userInfo?: { email?: string; name?: string }
  ) => void;
  /**
   * KIR-3495: restrict every row to a single org member (Shadow AI user-scope
   * page). Omit for the org-wide table.
   */
  scopeUserId?: string;
  /**
   * Overrides the table id, which keys URL state and local preferences. The
   * user-scope page passes its own so its state never collides with the
   * org-wide table's.
   */
  tableId?: string;
  /** Seeds the initially expanded rows (deep links from the org table). */
  initialState?: DataTableProps<RunningNowListItem>['initialState'];
}

export function DataTableRunningNowContent({
  organizationId,
  onUserClick,
  scopeUserId,
  tableId = RUNNING_NOW_TABLE_CONFIG.tableId,
  initialState,
}: DataTableRunningNowContentProps): ReactElement {
  const [rootOnly, setRootOnly] = useState(false);

  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.SHADOW_AI_LOGGED_IN_USER,
    flagsmithFeatureFlags.ENDPOINT_IDENTIFIER,
  ]);
  const showLoggedInUser =
    flags[flagsmithFeatureFlags.SHADOW_AI_LOGGED_IN_USER] ?? false;
  const showEndpointName =
    flags[flagsmithFeatureFlags.ENDPOINT_IDENTIFIER] ?? false;

  const approvalTable = useToolApprovalTable({
    organizationId,
    tableId,
    tab: TOOL_APPROVAL_TABS.RUNNING,
    scope: TOOL_APPROVAL_SCOPES.ENDPOINT,
    getRowId: getRunningNowRowId,
    getToolId: getRunningNowToolId,
    getRowLabel: getRunningNowRowLabel,
    scopeUserId,
    countsRefetchInterval: RUNNING_PROCESSES_REFRESH_INTERVAL,
  });
  const { approvalFilter, writeGate } = approvalTable;
  const approvalEnabled = approvalTable.enabled;

  const columns = useMemo(() => {
    const base = createRunningNowColumns(
      organizationId,
      onUserClick,
      showLoggedInUser,
      scopeUserId,
      showEndpointName
    );
    if (!approvalEnabled) return base;
    return [
      ...base,
      createToolApprovalColumn<RunningNowListItem>({
        getToolId: getRunningNowToolId,
        scope: TOOL_APPROVAL_SCOPES.ENDPOINT,
        organizationId,
        writeGate,
      }),
    ];
  }, [
    organizationId,
    onUserClick,
    showLoggedInUser,
    scopeUserId,
    showEndpointName,
    approvalEnabled,
    writeGate,
  ]);

  // Include organizationId in countScope so knownTotal cache is invalidated
  // when switching orgs (the component is not remounted on org change), and
  // scopeUserId so it is invalidated when drilling into a different member.
  const countScope = useMemo(
    () => ({ organizationId, scopeUserId, approval: approvalFilter }),
    [organizationId, scopeUserId, approvalFilter]
  );

  const queryFn = useCallback(
    (params: DataFetchParams, signal?: AbortSignal) =>
      fetchRunningNow(
        organizationId,
        params,
        signal,
        rootOnly ? true : undefined,
        scopeUserId,
        approvalFilter
      ),
    [organizationId, rootOnly, scopeUserId, approvalFilter]
  );

  const queryKeyFn = useCallback(
    (params: DataFetchParams) =>
      buildQueryKeys(
        organizationId,
        params,
        rootOnly,
        scopeUserId,
        approvalFilter
      ),
    [organizationId, rootOnly, scopeUserId, approvalFilter]
  );

  const renderExpandedContent = useCallback(
    (row: RunningNowListItem) => <ExpandedProcessRow row={row} />,
    []
  );

  const searchConfig = useMemo(
    () => ({
      placeholder: 'Search running processes...',
      className: 'w-[260px]',
    }),
    []
  );

  const featuresConfig = useMemo(
    () => ({
      enableSorting: true,
      enableRowExpansion: true,
      // KIR-3495: the user-scope page stacks all three tables at once. Each
      // table syncing its state into the URL makes every table re-render on
      // every other table's sync, which blows up superlinearly, so the scoped
      // page trades deep-linkable filters for a page that renders.
      enableUrlState: !scopeUserId,
    }),
    [scopeUserId]
  );

  const rowExpansionConfig = useMemo(
    () => ({
      getRowId: getRunningNowRowId,
      render: renderExpandedContent,
      contentClassName: 'bg-muted/30 p-4',
      rowClassName: 'bg-muted/10',
      keepMounted: false as const,
      showContainer: false as const,
      highlightOnExpand: true,
      scrollToExpandedRow: true,
    }),
    [renderExpandedContent]
  );

  const queryConfig = useMemo(
    () => ({
      staleTime: 15000,
      refetchOnWindowFocus: true,
      refetchInterval: RUNNING_PROCESSES_REFRESH_INTERVAL,
    }),
    []
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Running Now</CardTitle>
          <div className="flex items-center gap-2">
            <Switch
              id="rootOnly"
              checked={rootOnly}
              onCheckedChange={setRootOnly}
            />
            <label
              htmlFor="rootOnly"
              className="text-sm text-muted-foreground cursor-pointer"
            >
              Root processes only
            </label>
          </div>
        </div>
        <CardDescription>{RUNNING_NOW_NOTE}</CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        {approvalTable.isFlagLoading ? (
          <div data-testid="tool-approval-flag-loading">
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <DataTable<RunningNowListItem>
            key={rootOnly ? 'root-only' : 'all'}
            id={tableId}
            mode="server"
            initialState={initialState}
            className="flex flex-col"
            containerClassName={SHADOW_AI_TABLE_SCROLL_CLASS}
            columns={columns}
            errorBoundaryLabel="Something went wrong in the Running Now table"
            search={searchConfig}
            features={featuresConfig}
            rowExpansion={rowExpansionConfig}
            queryFn={queryFn}
            getQueryKeysFromParams={queryKeyFn}
            noDataLabel="No running processes found."
            queryConfig={queryConfig}
            countScope={countScope}
            {...approvalTable.dataTableProps}
          />
        )}
      </CardContent>
    </Card>
  );
}

DataTableRunningNowContent.displayName = 'DataTableRunningNowContent';
